/**
 * Shared form helpers for the Cash Flows dialogs (convert-form conventions):
 * Standard number format (#,##0.00), readonly field styling, em-dash combo
 * labels. Mirrors the recipe in the /convert-form skill.
 */
import type { SxProps, Theme } from '@mui/material/styles'

/** Right-aligned numeric input (Access TextAlign=Right + decimal keypad). */
export const numericInputProps = {
  style: { textAlign: 'right' as const },
  inputMode: 'decimal' as const,
}

/**
 * Compact DialogContent styling — mirrors the canonical convert-form dialog
 * (`LocalPFormDialog`): input/label font 0.8125rem + min-height 32 so fields
 * read tight, not the theme-default ~0.9375rem (which looked oversized here).
 * Apply on every Cash Flows / Misa dialog `<DialogContent sx={...}>`.
 */
export const compactDialogContentSx: SxProps<Theme> = {
  pt: 0.75,
  '& .MuiInputBase-root': { minHeight: 32, fontSize: '0.8125rem' },
  '& .MuiInputBase-input': { py: 0.5, fontSize: '0.8125rem' },
  '& .MuiInputLabel-root': { fontSize: '0.8125rem', fontWeight: 500 },
  '& .MuiInputLabel-shrink': { fontWeight: 600 },
  '& .MuiSvgIcon-root': { fontSize: '1rem' },
  '& .MuiSelect-select': { fontSize: '0.8125rem' },
  '& .MuiFormHelperText-root': { fontSize: '0.6875rem', mt: 0.25 },
}

/** Readonly snapshot field — grey bg so it reads as non-editable. */
export const readonlyFieldSx: SxProps<Theme> = {
  '& .MuiInputBase-root': {
    bgcolor: (t) => (t.palette.mode === 'dark' ? 'rgba(255,255,255,0.06)' : '#f1f3f5'),
  },
  '& .MuiInputBase-input': {
    color: (t) => t.palette.text.secondary,
    fontWeight: 600,
  },
}

/** Parse "1,234.5" → 1234.5; "" → null. */
export function parseNumeric(v: string | number | null | undefined): number | null {
  if (typeof v === 'number') return Number.isFinite(v) ? v : null
  const s = (v ?? '').toString().replace(/,/g, '').trim()
  if (!s) return null
  const n = parseFloat(s)
  return Number.isFinite(n) ? n : null
}

/** Format raw → "1,234.56" (Access Standard). */
export function formatStandard(v: number | string | null | undefined): string {
  if (v === null || v === undefined || v === '') return ''
  const n = typeof v === 'number' ? v : parseNumeric(String(v))
  if (n == null) return ''
  return n.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
}

/** Thousand-separated, 0 decimals — for Quantity / Amount input fields. */
export function formatInteger(v: number | string | null | undefined): string {
  if (v === null || v === undefined || v === '') return ''
  const n = typeof v === 'number' ? v : parseNumeric(String(v))
  if (n == null) return ''
  return n.toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 })
}
